import React, { useState, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { requestPasswordReset } from '../api/services';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const validateEmail = (email) => {
    if (!email) {
      return 'Email is required';
    }
    if (!EMAIL_REGEX.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value.trim());
    setError('');
    setSuccess('');
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');
      setSuccess('');

      const emailError = validateEmail(email);
      if (emailError) {
        setError(emailError);
        setLoading(false);
        return;
      }

      try {
        await requestPasswordReset(email);
        setSuccess('OTP has been requested. Please contact your admin to receive the OTP.');
        setTimeout(() => {
          navigate('/reset-password', { state: { email } });
        }, 2000);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          (err.code === 'ERR_NETWORK'
            ? 'Network error, please try again later'
            : 'Failed to request OTP. Please try again.');
        setError(errorMessage);
        setLoading(false);
      }
    },
    [email, navigate]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && !validateEmail(email)) {
      handleSubmit(e);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
      role="region"
      aria-label="Forgot Password"
    >
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          py: 8,
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Forgot Password
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Enter your email to request a one-time password (OTP) from the admin.
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setError('')}
                role="alert"
                aria-live="assertive"
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => setSuccess('')}
                role="alert"
                aria-live="polite"
              >
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                id="email-input"
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                required
                margin="normal"
                disabled={loading}
                variant="outlined"
                InputProps={{ sx: { borderRadius: 2 } }}
                helperText="Enter the email associated with your account"
                aria-label="Email address for password reset"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  backgroundColor: '#009db9',
                  fontSize: '1rem',
                  '&:hover': {
                    backgroundColor: '#007a8c',
                  },
                }}
                disabled={loading || !!validateEmail(email)}
                aria-label="Request OTP"
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Request OTP'}
              </Button>
              <Button
                fullWidth
                variant="text"
                sx={{ mt: 2, textTransform: 'none' }}
                onClick={() => navigate('/login')}
                disabled={loading}
                aria-label="Back to login"
              >
                Back to Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
